/**
 * @progression/i18n (L5) — the language layer that realizes INV-STABLE-ID.
 *
 * Turns stable `MessageId`s into localized strings via per-locale `Catalog`s, with
 * direction, ICU message formatting, Intl number/date formatting, per-script font stacks,
 * and the bidi discipline that keeps technical ids LTR inside RTL text. It decorates the
 * L4 view-model's output only — the verdict factors through the id, so swapping the
 * catalog (even a mistranslation) leaves every verdict fixed
 * (ArchitectureV2.lean §3: `stable_id_translation_inert`).
 *
 * Owner: Agent 6. Depends only on `@progression/contracts` (the §6.4/§6.5 types). The L4
 * view-model *types* live in contracts; this package consumes view-models at runtime
 * through those types and never imports upward.
 */
// Provider (Deliverable A + C)
export { CatalogI18nProvider, createI18nProvider } from './provider.js';
// CANONICAL catalogs (Phase 3): English (= Agent 5's published key set), Arabic (RTL),
// Hindi (Devanagari). `EN_PHASE2_CATALOG` is a deprecated alias of `EN_CATALOG`.
export { EN_CATALOG, EN_PHASE2_CATALOG } from './catalogs/en.js';
export { AR_CATALOG } from './catalogs/ar.js';
export { HI_CATALOG } from './catalogs/hi.js';
// Full language integration, batch 1 (Agent 9): seven native-grade locales added as pure
// catalog drop-ins (zero core change, INV-STABLE-ID). French / German / Spanish / Dutch /
// Russian / Chinese (Simplified) / Greek — each covering the full canonical key set with the
// locale's exact CLDR pluralization and correct script/font/`lang` wiring.
export { FR_CATALOG } from './catalogs/fr.js';
export { DE_CATALOG } from './catalogs/de.js';
export { ES_CATALOG } from './catalogs/es.js';
export { NL_CATALOG } from './catalogs/nl.js';
export { RU_CATALOG } from './catalogs/ru.js';
export { ZH_HANS_CATALOG } from './catalogs/zh-Hans.js';
export { EL_CATALOG } from './catalogs/el.js';
// Full language integration, batch 2 (Agent 9): twenty-six further native-grade locales added
// as pure catalog drop-ins (zero core change, INV-STABLE-ID) — the EU/EEA official languages
// plus the Celtic family (Irish, Scottish Gaelic, Welsh, Breton, Cornish), Albanian and
// Armenian. Each covers the full canonical key set with the locale's exact CLDR pluralization
// and correct script/font/`lang` wiring (Armenian adds a new script to `scriptOf`/`font-stacks`).
export { BG_CATALOG } from './catalogs/bg.js';
export { HR_CATALOG } from './catalogs/hr.js';
export { CS_CATALOG } from './catalogs/cs.js';
export { DA_CATALOG } from './catalogs/da.js';
export { ET_CATALOG } from './catalogs/et.js';
export { FI_CATALOG } from './catalogs/fi.js';
export { HU_CATALOG } from './catalogs/hu.js';
export { GA_CATALOG } from './catalogs/ga.js';
export { IT_CATALOG } from './catalogs/it.js';
export { LV_CATALOG } from './catalogs/lv.js';
export { LT_CATALOG } from './catalogs/lt.js';
export { MT_CATALOG } from './catalogs/mt.js';
export { PL_CATALOG } from './catalogs/pl.js';
export { PT_CATALOG } from './catalogs/pt.js';
export { RO_CATALOG } from './catalogs/ro.js';
export { SK_CATALOG } from './catalogs/sk.js';
export { SL_CATALOG } from './catalogs/sl.js';
export { SV_CATALOG } from './catalogs/sv.js';
export { NB_CATALOG } from './catalogs/nb.js';
export { IS_CATALOG } from './catalogs/is.js';
export { GD_CATALOG } from './catalogs/gd.js';
export { CY_CATALOG } from './catalogs/cy.js';
export { BR_CATALOG } from './catalogs/br.js';
export { KW_CATALOG } from './catalogs/kw.js';
export { SQ_CATALOG } from './catalogs/sq.js';
export { HY_CATALOG } from './catalogs/hy.js';
// Full language integration, batch 3 (Agent 9): twenty African-language locales added as pure
// catalog drop-ins (zero core change, INV-STABLE-ID). Amharic + Tigrinya add the Ethiopic
// (Geʽez) script to `scriptOf`/`font-stacks`. The remaining batch-3 languages requested but not
// shipped are recorded, with reasons, in `Agent9_Batch3_REPORT.md` (the flagged skip list).
export { SW_CATALOG } from './catalogs/sw.js';
export { HA_CATALOG } from './catalogs/ha.js';
export { AM_CATALOG } from './catalogs/am.js';
export { TI_CATALOG } from './catalogs/ti.js';
export { YO_CATALOG } from './catalogs/yo.js';
export { IG_CATALOG } from './catalogs/ig.js';
export { ZU_CATALOG } from './catalogs/zu.js';
export { XH_CATALOG } from './catalogs/xh.js';
export { SN_CATALOG } from './catalogs/sn.js';
export { MG_CATALOG } from './catalogs/mg.js';
export { NY_CATALOG } from './catalogs/ny.js';
export { RW_CATALOG } from './catalogs/rw.js';
export { RN_CATALOG } from './catalogs/rn.js';
export { LN_CATALOG } from './catalogs/ln.js';
export { ST_CATALOG } from './catalogs/st.js';
export { TN_CATALOG } from './catalogs/tn.js';
export { SS_CATALOG } from './catalogs/ss.js';
export { WO_CATALOG } from './catalogs/wo.js';
export { LG_CATALOG } from './catalogs/lg.js';
export { AK_CATALOG } from './catalogs/ak.js';
// The "add your language" path (Deliverable E): a complete placeholder catalog, the
// canonical key set, and a parity check — adding a language stays a pure catalog drop-in.
export { TEMPLATE_CATALOG, TEMPLATE_PLACEHOLDER, CANONICAL_KEYS, parityOf, } from './catalogs/template.js';
// CANONICAL stable-id vocabulary (mirrors view/messages.ts + §9.1 domain labels + compute).
export { P2, P2_ID, VIEW_MSG, DOMAIN_MSG, COMPUTE_MSG, PHASE2_MESSAGE_IDS, PHASE2_MESSAGE_ID_SET, } from './message-ids-phase2.js';
// Locale-aware computational-result formatting (Compute Integration, Deliverable B).
export { formatResult } from './format-result.js';
// Retired Phase-1 vocabulary — kept only as an internal/deprecated fixture (see DECISIONS
// P3-D1). `EN_LEGACY_CATALOG` is NOT part of the canonical/shipped/gated surface.
export { EN_CATALOG as EN_LEGACY_CATALOG } from './catalog-en.js';
export { MSG as LEGACY_MSG, MSG_ID, PHASE1_MESSAGE_IDS, PHASE1_MESSAGE_ID_SET, } from './message-ids.js';
import { EN_CATALOG } from './catalogs/en.js';
import { AR_CATALOG } from './catalogs/ar.js';
import { HI_CATALOG } from './catalogs/hi.js';
import { FR_CATALOG } from './catalogs/fr.js';
import { DE_CATALOG } from './catalogs/de.js';
import { ES_CATALOG } from './catalogs/es.js';
import { NL_CATALOG } from './catalogs/nl.js';
import { RU_CATALOG } from './catalogs/ru.js';
import { ZH_HANS_CATALOG } from './catalogs/zh-Hans.js';
import { EL_CATALOG } from './catalogs/el.js';
import { BG_CATALOG } from './catalogs/bg.js';
import { HR_CATALOG } from './catalogs/hr.js';
import { CS_CATALOG } from './catalogs/cs.js';
import { DA_CATALOG } from './catalogs/da.js';
import { ET_CATALOG } from './catalogs/et.js';
import { FI_CATALOG } from './catalogs/fi.js';
import { HU_CATALOG } from './catalogs/hu.js';
import { GA_CATALOG } from './catalogs/ga.js';
import { IT_CATALOG } from './catalogs/it.js';
import { LV_CATALOG } from './catalogs/lv.js';
import { LT_CATALOG } from './catalogs/lt.js';
import { MT_CATALOG } from './catalogs/mt.js';
import { PL_CATALOG } from './catalogs/pl.js';
import { PT_CATALOG } from './catalogs/pt.js';
import { RO_CATALOG } from './catalogs/ro.js';
import { SK_CATALOG } from './catalogs/sk.js';
import { SL_CATALOG } from './catalogs/sl.js';
import { SV_CATALOG } from './catalogs/sv.js';
import { NB_CATALOG } from './catalogs/nb.js';
import { IS_CATALOG } from './catalogs/is.js';
import { GD_CATALOG } from './catalogs/gd.js';
import { CY_CATALOG } from './catalogs/cy.js';
import { BR_CATALOG } from './catalogs/br.js';
import { KW_CATALOG } from './catalogs/kw.js';
import { SQ_CATALOG } from './catalogs/sq.js';
import { HY_CATALOG } from './catalogs/hy.js';
import { SW_CATALOG } from './catalogs/sw.js';
import { HA_CATALOG } from './catalogs/ha.js';
import { AM_CATALOG } from './catalogs/am.js';
import { TI_CATALOG } from './catalogs/ti.js';
import { YO_CATALOG } from './catalogs/yo.js';
import { IG_CATALOG } from './catalogs/ig.js';
import { ZU_CATALOG } from './catalogs/zu.js';
import { XH_CATALOG } from './catalogs/xh.js';
import { SN_CATALOG } from './catalogs/sn.js';
import { MG_CATALOG } from './catalogs/mg.js';
import { NY_CATALOG } from './catalogs/ny.js';
import { RW_CATALOG } from './catalogs/rw.js';
import { RN_CATALOG } from './catalogs/rn.js';
import { LN_CATALOG } from './catalogs/ln.js';
import { ST_CATALOG } from './catalogs/st.js';
import { TN_CATALOG } from './catalogs/tn.js';
import { SS_CATALOG } from './catalogs/ss.js';
import { WO_CATALOG } from './catalogs/wo.js';
import { LG_CATALOG } from './catalogs/lg.js';
import { AK_CATALOG } from './catalogs/ak.js';
/** The canonical shipped locales, ready to hand to a provider or a host (en, ar, hi). */
export const PHASE2_CATALOGS = [EN_CATALOG, AR_CATALOG, HI_CATALOG];
/** Alias: the canonical locale set (preferred name going forward). */
export const CANONICAL_CATALOGS = PHASE2_CATALOGS;
/**
 * Agent 9 batch 1 — the seven fully-integrated locales (fr, de, es, nl, ru, zh-Hans, el).
 * A separate export so the original `PHASE2_CATALOGS`/`CANONICAL_CATALOGS` trio is untouched
 * for existing neighbours (host-bundle, integration), while a host that wants the broader set
 * can hand `ALL_CATALOGS` to a provider. Adding a language never changes a verdict.
 */
export const BATCH1_CATALOGS = [
    FR_CATALOG,
    DE_CATALOG,
    ES_CATALOG,
    NL_CATALOG,
    RU_CATALOG,
    ZH_HANS_CATALOG,
    EL_CATALOG,
];
/**
 * Agent 9 batch 2 — the twenty-six further locales. Like batch 1, a separate export so the
 * canonical trio and `BATCH1_CATALOGS` stay untouched for existing neighbours; a host that
 * wants the broad set hands `ALL_CATALOGS` to a provider. Adding a language never moves a verdict.
 */
export const BATCH2_CATALOGS = [
    BG_CATALOG,
    HR_CATALOG,
    CS_CATALOG,
    DA_CATALOG,
    ET_CATALOG,
    FI_CATALOG,
    HU_CATALOG,
    GA_CATALOG,
    IT_CATALOG,
    LV_CATALOG,
    LT_CATALOG,
    MT_CATALOG,
    PL_CATALOG,
    PT_CATALOG,
    RO_CATALOG,
    SK_CATALOG,
    SL_CATALOG,
    SV_CATALOG,
    NB_CATALOG,
    IS_CATALOG,
    GD_CATALOG,
    CY_CATALOG,
    BR_CATALOG,
    KW_CATALOG,
    SQ_CATALOG,
    HY_CATALOG,
];
/**
 * Agent 9 batch 3 — twenty African-language locales. Like the earlier batches, a separate
 * export so the canonical trio and `BATCH1`/`BATCH2` stay untouched for existing neighbours.
 * Amharic & Tigrinya are Ethiopic-script; Yoruba/Igbo/Wolof are `other`-only plurals.
 */
export const BATCH3_CATALOGS = [
    SW_CATALOG,
    HA_CATALOG,
    AM_CATALOG,
    TI_CATALOG,
    YO_CATALOG,
    IG_CATALOG,
    ZU_CATALOG,
    XH_CATALOG,
    SN_CATALOG,
    MG_CATALOG,
    NY_CATALOG,
    RW_CATALOG,
    RN_CATALOG,
    LN_CATALOG,
    ST_CATALOG,
    TN_CATALOG,
    SS_CATALOG,
    WO_CATALOG,
    LG_CATALOG,
    AK_CATALOG,
];
/**
 * Every catalog shipped so far: the canonical trio (en/ar/hi) plus Agent 9's batch 1 (7),
 * batch 2 (26) and batch 3 (20) — fifty-six locales in all.
 */
export const ALL_CATALOGS = [
    ...PHASE2_CATALOGS,
    ...BATCH1_CATALOGS,
    ...BATCH2_CATALOGS,
    ...BATCH3_CATALOGS,
];
// Direction / bidi discipline (Deliverable D)
export { directionOf, primarySubtag, isolateLtr, isolateAuto, stripBidi, bdiLtr, FSI, LRI, RLI, PDI, } from './bidi.js';
// Per-script font stacks (Deliverable D)
export { fontStackFor, KNOWN_SCRIPTS } from './font-stacks.js';
// Script resolution (Deliverable A + C) — `scriptOf(locale)`, script-subtag aware.
export { scriptOf, NON_LATIN_LANGUAGES } from './script.js';
// Pseudo-localisation (Deliverable D/E — L5's Machine lane), incl. RTL pseudo (Phase 2 D)
export { makePseudoCatalog, makeRtlPseudoCatalog, pseudoPattern } from './pseudo.js';
import { makePseudoCatalog, makeRtlPseudoCatalog } from './pseudo.js';
/** Expanded+accented LTR pseudo-locale over the canonical catalog (Agent 2 pseudo-loc lane). */
export const PSEUDO_LTR_CATALOG = makePseudoCatalog(EN_CATALOG, 'en-XA');
/** Expanded RTL pseudo-locale (direction-flip) over the canonical catalog (Agent 2 bidi lane). */
export const PSEUDO_RTL_CATALOG = makeRtlPseudoCatalog(EN_CATALOG, 'ar-XB');
/** Ready-made pseudo-locales for Agent 2's pseudo-loc/bidi CI gate. */
export const PSEUDO_CATALOGS = [PSEUDO_LTR_CATALOG, PSEUDO_RTL_CATALOG];
//# sourceMappingURL=index.js.map